import { memo, useState, useRef, useCallback } from 'react'
import { Slider } from '@/components/ui/slider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface MortgageSliderProps {
  label: string
  value: number
  min: number
  max: number
  step: number
  formatValue: (value: number) => string
  onChange: (value: number) => void
  tooltip?: string
  secondaryLabel?: string
  icon?: React.ReactNode
}

export const MortgageSlider = memo(function MortgageSlider({
  label,
  value,
  min,
  max,
  step,
  formatValue,
  onChange,
  tooltip,
  secondaryLabel,
  icon,
}: MortgageSliderProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const startEditing = useCallback(() => {
    setEditText(String(value))
    setIsEditing(true)
    requestAnimationFrame(() => inputRef.current?.select())
  }, [value])

  const confirmEdit = useCallback(() => {
    const parsed = parseFloat(editText.replace(/[^\d.,\-]/g, '').replace(',', '.'))
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed))
      onChange(clamped)
    }
    setIsEditing(false)
  }, [editText, min, max, step, onChange])

  const cancelEdit = useCallback(() => setIsEditing(false), [])

  return (
    <div className="space-y-3 group">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon && (
            <span className="text-muted-foreground group-hover:text-primary transition-colors duration-200">
              {icon}
            </span>
          )}
          {tooltip ? (
            <Tooltip>
              <TooltipTrigger>
                <span className="text-sm font-medium text-foreground cursor-help border-b border-dashed border-muted-foreground/50">
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-sm font-medium text-foreground">{label}</span>
          )}
        </div>
        <div className="text-right">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmEdit()
                if (e.key === 'Escape') cancelEdit()
              }}
              onBlur={confirmEdit}
              className="w-28 text-right text-lg font-bold bg-white border border-primary rounded-md px-2 py-0.5 outline-none focus:ring-2 focus:ring-primary/30 tabular-nums"
            />
          ) : (
            <span
              onClick={startEditing}
              className="text-lg font-bold bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] bg-clip-text text-transparent inline-block tabular-nums cursor-pointer hover:underline decoration-[#6366f1]/40 underline-offset-2"
              title="Kliknij, aby wpisać wartość"
            >
              {formatValue(value)}
            </span>
          )}
          {secondaryLabel && (
            <p className="text-xs text-muted-foreground">{secondaryLabel}</p>
          )}
        </div>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(Array.isArray(v) ? v[0] : v)}
        aria-label={label}
        className="cursor-pointer"
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  )
})
